import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { firebase } from "@core/firebase/app";
import type {
  BookingSummary,
  ChatDoc,
  ChatMessage,
  ChatMessageDoc,
  ChatProfile,
  ChatSummary,
  SendChatMessagePayload,
  SendChatMessageResponse,
  UserSummary,
} from "./types";
import { queryKeys } from "@core/query";
import { createQuery } from "@tanstack/solid-query";

const CHATS_COLLECTION = "chats";
const MESSAGES_COLLECTION = "messages";
const USERS_COLLECTION = "users";
const BOOKINGS_COLLECTION = "bookings";
const sendChatMessageCallable = httpsCallable<SendChatMessagePayload, SendChatMessageResponse>(
  firebase.functions,
  "sendChatMessage",
);

const toDate = (value: unknown): Date | null => {
  if (value instanceof Timestamp) {
    return value.toDate();
  }
  if (typeof value === "number") {
    return new Date(value);
  }
  if (typeof value === "object" && value && "toDate" in (value as Record<string, unknown>)) {
    try {
      return (value as { toDate: () => Date }).toDate();
    } catch (error) {
      return null;
    }
  }
  return null;
};

const mapChatSummary = (id: string, data: ChatDoc): ChatSummary => {
  const unreadAdmin = data.unreadCounts?.admin ?? 0;
  const lastMessageAt = toDate(data.lastMessageAt ?? data.lastMessage?.createdAt ?? data.updatedAt);
  return {
    id,
    status: data.status,
    userId: data.userId ?? null,
    anonSessionId: data.anonSessionId ?? null,
    lastMessagePreview: data.lastMessage?.preview ?? null,
    lastMessageSenderType: data.lastMessage?.senderType ?? null,
    lastMessageAt,
    unreadForAdmin: typeof unreadAdmin === "number" ? unreadAdmin : 0,
    updatedAt: toDate(data.updatedAt),
    createdAt: toDate(data.createdAt),
  };
};

const mapChatMessage = (id: string, data: ChatMessageDoc): ChatMessage => ({
  id,
  senderType: data.senderType,
  senderId: data.senderId ?? null,
  text: data.text,
  createdAt: toDate(data.createdAt),
  deliveryStatus: data.deliveryStatus,
});

const mapUserSummary = (id: string, data: Record<string, unknown>): UserSummary => {
  const statsRaw = typeof data.stats === "object" && data.stats !== null ? (data.stats as Record<string, unknown>) : null;
  const totalCleanings = statsRaw && typeof statsRaw["totalCleanings"] === "number" ? (statsRaw["totalCleanings"] as number) : undefined;
  const totalSquareFootCleaned =
    statsRaw && typeof statsRaw["totalSquareFootCleaned"] === "number"
      ? (statsRaw["totalSquareFootCleaned"] as number)
      : undefined;

  const stats =
    totalCleanings === undefined && totalSquareFootCleaned === undefined
      ? null
      : {
          totalCleanings,
          totalSquareFootCleaned,
        };

  return {
    id,
    displayName:
      typeof data.displayName === "string"
        ? (data.displayName as string)
        : typeof data.email === "string"
        ? (data.email as string)
        : null,
    email: typeof data.email === "string" ? (data.email as string) : null,
    phoneNumber: typeof data.phoneNumber === "string" ? (data.phoneNumber as string) : null,
    photoURL: typeof data.photoURL === "string" ? (data.photoURL as string) : undefined,
    stats,
  };
};

const mapBookingSummary = (id: string, raw: Record<string, unknown>): BookingSummary => {
  const schedule = typeof raw.schedule === "object" && raw.schedule !== null ? (raw.schedule as Record<string, unknown>) : null;
  return {
    id,
    status: typeof raw.status === "string" ? (raw.status as string) : "unknown",
    serviceId: typeof raw.serviceId === "string" ? (raw.serviceId as string) : null,
    roomType: typeof raw.roomType === "string" ? (raw.roomType as string) : null,
    amount: typeof raw.amount === "number" ? (raw.amount as number) : null,
    currency: typeof raw.currency === "string" ? (raw.currency as string) : null,
    createdAt: toDate(raw.createdAt),
    scheduledDate: schedule && typeof schedule["scheduledDate"] === "string" ? (schedule["scheduledDate"] as string) : null,
    timeSlotLabel: schedule && typeof schedule["timeSlotLabel"] === "string" ? (schedule["timeSlotLabel"] as string) : null,
  };
};

export const fetchRecentChats = async (opts?: { status?: string; limit?: number }): Promise<ChatSummary[]> => {
  const collectionRef = collection(firebase.firestore, CHATS_COLLECTION);
  const chatQuery = query(
    collectionRef,
    orderBy("lastMessageAt", "desc"),
    limit(opts?.limit ?? 30),
  );

  const snapshot = await getDocs(chatQuery);
  return snapshot.docs.map((docSnap) => mapChatSummary(docSnap.id, docSnap.data() as ChatDoc));
};

export const fetchChatMessages = async (chatId: string): Promise<ChatMessage[]> => {
  const messagesRef = collection(firebase.firestore, CHATS_COLLECTION, chatId, MESSAGES_COLLECTION);
  const messagesQuery = query(messagesRef, orderBy("createdAt", "asc"), limit(200));
  const snapshot = await getDocs(messagesQuery);
  return snapshot.docs.map((docSnap) => mapChatMessage(docSnap.id, docSnap.data() as ChatMessageDoc));
};

export const fetchChatById = async (chatId: string): Promise<ChatSummary | null> => {
  const chatRef = doc(firebase.firestore, CHATS_COLLECTION, chatId);
  const snapshot = await getDoc(chatRef);
  if (!snapshot.exists()) {
    return null;
  }
  return mapChatSummary(snapshot.id, snapshot.data() as ChatDoc);
};

const fetchUserSummary = async (userId: string): Promise<UserSummary | null> => {
  const userRef = doc(firebase.firestore, USERS_COLLECTION, userId);
  const snapshot = await getDoc(userRef);
  if (!snapshot.exists()) {
    return null;
  }
  return mapUserSummary(snapshot.id, snapshot.data() as Record<string, unknown>);
};

const fetchUserBookings = async (userId: string, take = 5): Promise<BookingSummary[]> => {
  const bookingsRef = collection(firebase.firestore, BOOKINGS_COLLECTION);
  const bookingsQuery = query(bookingsRef, where("userId", "==", userId), orderBy("createdAt", "desc"), limit(take));
  const snapshot = await getDocs(bookingsQuery);
  return snapshot.docs.map((docSnap) => mapBookingSummary(docSnap.id, docSnap.data() as Record<string, unknown>));
};

export const fetchChatProfile = async (chatId: string): Promise<ChatProfile | null> => {
  const chat = await fetchChatById(chatId);
  if (!chat) {
    return null;
  }

  if (!chat.userId) {
    return { chat, user: null, bookings: [] };
  }

  const [user, bookings] = await Promise.all([
    fetchUserSummary(chat.userId),
    fetchUserBookings(chat.userId),
  ]);

  return {
    chat,
    user,
    bookings,
  };
};

export const sendChatMessage = async (payload: SendChatMessagePayload): Promise<ChatMessage> => {
  const response = await sendChatMessageCallable(payload);
  const data = response.data;

  return {
    id: data.messageId,
    senderType: data.senderType,
    senderId: firebase.auth.currentUser?.uid ?? null,
    text: data.text,
    createdAt: toDate(data.createdAt) ?? new Date(data.createdAt),
    deliveryStatus: "sent",
  };
};
export const useChatListQuery = () =>
  createQuery(() => ({
    queryKey: queryKeys.chats.root(),
    queryFn: () => fetchRecentChats(),
  }));

export const useChatMessagesQuery = (chatId: () => string | null) =>
  createQuery(() => {
    const id = chatId();
    return {
      queryKey: queryKeys.chats.messages(id ?? "pending"),
      enabled: Boolean(id),
      queryFn: () => (id ? fetchChatMessages(id) : Promise.resolve([] as ChatMessage[])),
    };
  });

export const useChatProfileQuery = (chatId: () => string | null) =>
  createQuery(() => {
    const id = chatId();
    return {
      queryKey: queryKeys.chats.detail(id ?? "profile"),
      enabled: Boolean(id),
      queryFn: () => (id ? fetchChatProfile(id) : Promise.resolve(null)),
    };
  });

export const useChatSummaryQuery = useChatProfileQuery;

